import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core'
import { MatDialogRef } from '@angular/material/dialog'
import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js'
import { environment } from 'src/environments/environment'
import { SettingsService } from '../../settings/settings.service'

/**
 * Hosts Stripe's embedded `account-onboarding` component (via @stripe/connect-js)
 * so a merchant can provide their bank account and accept Stripe's ToS entirely
 * inside our own UI — they never see stripe.com. Opened right after
 * createStripeConnectAccount succeeds (see payment-methods.component.ts); the
 * account it operates on already exists, this only collects what's still
 * missing (see requirements.currently_due on the account).
 */
@Component({
  selector: 'app-stripe-connect-onboarding',
  templateUrl: './stripe-connect-onboarding.component.html',
  styleUrl: './stripe-connect-onboarding.component.scss'
})
export class StripeConnectOnboardingComponent implements AfterViewInit {
  @ViewChild('onboardingContainer', { static: true }) containerRef!: ElementRef<HTMLDivElement>

  loading = true
  error = ''

  private connectInstance: StripeConnectInstance | null = null

  constructor(
    private settingsService: SettingsService,
    public dialogRef: MatDialogRef<StripeConnectOnboardingComponent>
  ) {}

  ngAfterViewInit(): void {
    this.mountOnboarding()
  }

  private async mountOnboarding(): Promise<void> {
    try {
      this.connectInstance = loadConnectAndInitialize({
        // Stacks' own platform publishable key — same account as stacks-agents'
        // stripePlatformSecretKey, not a per-merchant key. Safe to ship client-side.
        publishableKey: environment.stripePublishableKey,
        fetchClientSecret: () => this.fetchClientSecret()
      })

      const onboarding = this.connectInstance.create('account-onboarding')
      // Default is currently_due only, which skips threshold requirements like
      // "company registration number once volume reaches X" (Stripe's "Payouts
      // paused soon") — the merchant would be asked for nothing. eventually_due
      // is a superset, so nothing currently_due is lost.
      onboarding.setCollectionOptions({ fields: 'eventually_due', futureRequirements: 'include' })
      onboarding.setOnExit(() => {
        // Merchant closed or finished the flow — close the dialog either way so
        // the parent can refresh status (getStripeConnectStatus) and show the
        // current onboarding_status / requirements, rather than guessing here.
        this.dialogRef.close(true)
      })

      this.containerRef.nativeElement.appendChild(onboarding)
      this.loading = false
    } catch (err) {
      console.error('Failed to mount Stripe Connect onboarding', err)
      this.error = 'Failed to load the onboarding form. Please try again.'
      this.loading = false
    }
  }

  private fetchClientSecret(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.settingsService.createStripeConnectAccountSession().subscribe({
        next: (res) => resolve(res.clientSecret),
        error: (err) => {
          console.error('Failed to create Stripe Connect account session', err)
          reject(err)
        }
      })
    })
  }

  close(): void {
    this.dialogRef.close(false)
  }
}
